import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {Search, Sun, Moon, Menu, X, LogIn, LogOut, LayoutDashboard} from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { useAuth, signOut } from '@/hooks/useAuth'
import { lumi } from '@/lib/lumi'
import AdminLoginModal from '@/components/AdminLoginModal'
import type { SiteSetting } from '@/types'

interface HeaderProps {
  onSearch?: (query: string) => void
}

export default function Header({ onSearch }: HeaderProps) {
  const { theme, toggleTheme } = useTheme()
  const { isAuthenticated } = useAuth()
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [loginModalOpen, setLoginModalOpen] = useState(false)
  const [socialLinks, setSocialLinks] = useState<SiteSetting[]>([])
  const [siteTitle, setSiteTitle] = useState('CARLOS FELIPE')
  const [headerImage, setHeaderImage] = useState<string>('')
  const [logoImage, setLogoImage] = useState<string>('')
  const searchRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const [socialRes, titleRes, headerRes, logoRes] = await Promise.all([
          lumi.entities.site_settings.list({ filter: { category: 'social' }, limit: 20 }),
          lumi.entities.site_settings.list({ filter: { key: 'site_title' }, limit: 1 }),
          lumi.entities.site_settings.list({ filter: { key: 'header_image' }, limit: 1 }),
          lumi.entities.site_settings.list({ filter: { key: 'logo_image' }, limit: 1 }),
        ])
        setSocialLinks(socialRes.list as SiteSetting[])
        if (titleRes.list[0]) setSiteTitle((titleRes.list[0] as SiteSetting).value)
        if (headerRes.list[0]) setHeaderImage((headerRes.list[0] as SiteSetting).value)
        if (logoRes.list[0]) setLogoImage((logoRes.list[0] as SiteSetting).value)
      } catch (e) {
        console.error('Erro ao carregar configurações do cabeçalho:', e)
      }
    }
    fetchSettings()
  }, [])

  useEffect(() => {
    if (searchOpen && searchRef.current) searchRef.current.focus()
  }, [searchOpen])

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      navigate(`/?busca=${encodeURIComponent(searchQuery.trim())}`)
      onSearch?.(searchQuery.trim())
      setSearchOpen(false)
      setSearchQuery('')
    }
  }

  const handleLogout = () => {
    signOut()
  }

  const getSocialIcon = (key: string) => {
    const icons: Record<string, string> = {
      facebook: 'M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z',
      twitter: 'M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z',
      instagram: 'M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z M17.5 6.5h.01 M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9A5.5 5.5 0 0 1 16.5 22h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2z',
      youtube: 'M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z M9.75 15.02l5.75-3.02-5.75-3.02v6.04z',
      linkedin: 'M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z M2 9h4v12H2z M4 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
      tiktok: 'M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5',
      whatsapp: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z',
    }
    const lowerKey = key.toLowerCase()
    for (const [name, path] of Object.entries(icons)) {
      if (lowerKey.includes(name)) return path
    }
    return 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71 M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71'
  }

  return (
    <>
      {/* ── Imagem do Header ── */}
      {headerImage && (
        <Link to="/" aria-label="Página inicial" className="block w-full overflow-hidden">
          <img
            src={headerImage}
            alt={siteTitle}
            className="w-full object-cover object-center"
            style={{ height: 'clamp(140px, 20vw, 300px)' }}
            loading="eager"
          />
        </Link>
      )}

      {/* ── Barra de navegação ── */}
      <header className="w-full bg-white/95 dark:bg-gray-950/95 glass border-b border-gray-200/80 dark:border-gray-800/80 sticky top-0 z-50 shadow-[0_1px_12px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_12px_rgba(0,0,0,0.3)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">

            {/* Logo */}
            <Link
              to="/"
              className={`flex items-center gap-2.5 flex-shrink-0 group ${headerImage ? 'md:hidden' : ''}`}
              aria-label="Página inicial"
            >
              {logoImage ? (
                <img src={logoImage} alt={siteTitle} className="h-9 w-auto object-contain transition-opacity group-hover:opacity-80" />
              ) : (
                <div className="w-9 h-9 bg-red-600 rounded-xl flex items-center justify-center shadow-sm group-hover:bg-red-700 transition-colors">
                  <span className="text-white font-black text-sm tracking-tight">CF</span>
                </div>
              )}
              <span className="font-bold text-gray-900 dark:text-white text-lg hidden sm:block leading-tight tracking-tight group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                {siteTitle}
              </span>
            </Link>

            {/* Acções */}
            <div className="flex items-center gap-0.5">
              {/* Redes Sociais — desktop */}
              {socialLinks.length > 0 && (
                <nav className="hidden md:flex items-center gap-0.5 mr-1.5" aria-label="Redes sociais">
                  {socialLinks.map((social) => (
                    <a
                      key={social._id}
                      href={social.value}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-all"
                      aria-label={social.key}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d={getSocialIcon(social.key)} />
                      </svg>
                    </a>
                  ))}
                </nav>
              )}

              {/* Separador visual */}
              {socialLinks.length > 0 && <div className="hidden md:block w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1.5" />}

              {/* Busca */}
              <div className="relative">
                {searchOpen ? (
                  <form onSubmit={handleSearchSubmit} className="flex items-center">
                    <input
                      ref={searchRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Pesquisar notícias..."
                      className="w-36 sm:w-52 md:w-64 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-l-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500/30 transition-all"
                      aria-label="Campo de pesquisa"
                    />
                    <button type="submit" className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-r-xl transition-colors" aria-label="Pesquisar">
                      <Search size={14} />
                    </button>
                    <button type="button" onClick={() => setSearchOpen(false)} className="ml-1 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" aria-label="Fechar pesquisa">
                      <X size={15} />
                    </button>
                  </form>
                ) : (
                  <button onClick={() => setSearchOpen(true)} className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all" aria-label="Abrir pesquisa">
                    <Search size={19} />
                  </button>
                )}
              </div>

              {/* Tema */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                aria-label={theme === 'dark' ? 'Ativar tema claro' : 'Ativar tema escuro'}
              >
                {theme === 'dark' ? <Sun size={19} /> : <Moon size={19} />}
              </button>

              {/* Login / Dashboard / Logout */}
              {isAuthenticated ? (
                <div className="flex items-center gap-0.5">
                  <Link
                    to="/admin"
                    className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                    aria-label="Ir para o painel"
                  >
                    <LayoutDashboard size={15} />
                    <span className="hidden lg:inline text-sm">Painel</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                    aria-label="Sair"
                    title="Sair"
                  >
                    <LogOut size={19} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setLoginModalOpen(true)}
                  className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                  aria-label="Entrar no painel"
                  title="Entrar"
                >
                  <LogIn size={19} />
                </button>
              )}

              {/* Menu mobile */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all"
                aria-label="Menu"
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X size={19} /> : <Menu size={19} />}
              </button>
            </div>
          </div>

          {/* Menu mobile expandido */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 border-t border-gray-100 dark:border-gray-800 pt-4 space-y-3 animate-fade-in">
              {socialLinks.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 px-1">Redes Sociais</p>
                  <div className="flex flex-wrap gap-1.5">
                    {socialLinks.map((social) => (
                      <a
                        key={social._id}
                        href={social.value}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-red-50 dark:hover:bg-red-950/40 hover:text-red-600 dark:hover:text-red-400 transition-all"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d={getSocialIcon(social.key)} />
                        </svg>
                        <span className="capitalize">{social.key}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {isAuthenticated && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  <LayoutDashboard size={15} />
                  Ir para o Painel
                </Link>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Modal de Login */}
      <AdminLoginModal isOpen={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
    </>
  )
}
